/* Web Speech API wrapper — speech-to-text (SpeechRecognition) +
 * text-to-speech (speechSynthesis). Works offline on Chrome/Edge/Safari
 * desktop & most Android browsers. No API keys, zero cost.
 *
 * Language codes follow BCP-47 (e.g. en-IN, hi-IN, ta-IN, mr-IN). We map our
 * app's 2-letter codes to the browser's expected tags.
 */

const LANG_BCP47 = {
  en: "en-IN",
  hi: "hi-IN",
  ta: "ta-IN",
  mr: "mr-IN",
};

export function speechLang(appLang) {
  return LANG_BCP47[appLang] || "en-IN";
}

/** Returns true if the browser can do speech-to-text at all. */
export function speechRecognitionSupported() {
  if (typeof window === "undefined") return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/** Returns true if the browser can synthesise speech. */
export function speechSynthesisSupported() {
  if (typeof window === "undefined") return false;
  return "speechSynthesis" in window;
}

/**
 * Create a reusable SpeechRecognition instance for the given language.
 * The caller owns start/stop and `onresult`/`onerror`/`onend` handlers.
 */
export function createRecognizer(appLang = "en") {
  const Ctor = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = speechLang(appLang);
  rec.interimResults = true;
  rec.continuous = false; // single utterance — tap mic again for next
  rec.maxAlternatives = 1;
  return rec;
}

/**
 * Speak a string out loud in the given language. Returns a Promise that
 * resolves when the utterance ends (or rejects on error). Cancels any ongoing
 * utterance first so successive calls don't queue up.
 */
export function speak(text, appLang = "en", opts = {}) {
  return new Promise((resolve, reject) => {
    if (!speechSynthesisSupported() || !text) {
      resolve();
      return;
    }
    try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    const utter = new SpeechSynthesisUtterance(stripMarkdown(text));
    utter.lang = speechLang(appLang);
    utter.rate = opts.rate ?? 1.0;
    utter.pitch = opts.pitch ?? 1.0;
    utter.volume = opts.volume ?? 1.0;

    // Prefer a native voice matching the language if available.
    try {
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find((v) => v.lang === utter.lang) || voices.find((v) => v.lang && v.lang.startsWith(appLang));
      if (match) utter.voice = match;
    } catch (_) { /* noop */ }

    utter.onend = () => resolve();
    utter.onerror = (e) => reject(e);
    window.speechSynthesis.speak(utter);
  });
}

export function stopSpeaking() {
  if (speechSynthesisSupported()) {
    try { window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
  }
}

/** Strip Markdown so TTS doesn't say "asterisk asterisk Overview asterisk asterisk". */
function stripMarkdown(md) {
  return String(md)
    .replace(/```[\s\S]*?```/g, " ")   // code fences
    .replace(/`([^`]+)`/g, "$1")         // inline code
    .replace(/\*\*([^*]+)\*\*/g, "$1")   // bold
    .replace(/\*([^*]+)\*/g, "$1")         // italic
    .replace(/__([^_]+)__/g, "$1")         // bold alt
    .replace(/^#+\s+/gm, "")               // headings
    .replace(/^[-•]\s+/gm, "")               // list bullets
    .replace(/\s{2,}/g, " ")
    .trim();
}
